/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { useState, useEffect } from 'react';
import { useAuthContext } from '@asgardeo/auth-react';
import { useHistory, useLocation } from 'react-router-dom';
import PreLoader from 'pages/preloader/PreLoader';
import { ChoreoSignInConfig } from 'utils/auth';
import { setCookie } from 'utils/browser';
import logger from 'utils/logger';
import generateUUID from 'utils/uuid';

export const IS_FROM_CONTEST_PAGE = 'isFromContestPage';

interface LocationState {
  returnToUrl: string;
  returnToOrg: string;
  displayOrgName: string;
}

function SignupEmbeddedHandler() {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const { signIn } = useAuthContext();
  const { state } = useLocation<LocationState>();
  const returnToUrl = state?.returnToUrl || '/';
  const returnToOrg = state?.returnToOrg || '';

  const handleSignUp = async (
    fidp: string,
    stateParam: { [key: string]: string },
    captchaInfo?: string,
    username?: string
  ) => {
    const encodedStateParam = btoa(JSON.stringify(stateParam));

    try {
      setIsLoading(true);
      const config: ChoreoSignInConfig = {
        fidp,
        state: encodedStateParam,
      };
      if (username) {
        config.username = username;
      }
      if (captchaInfo) {
        config.captchaInfo = captchaInfo;
      }
      const tokenRequestConfig = {
        params: {
          tokenBindingId: generateUUID(),
        },
      };
      signIn(
        config,
        undefined,
        undefined,
        undefined,
        undefined,
        tokenRequestConfig
      );
    } catch (signInError) {
      logger.error(signInError);
      history.push('/internal-error');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fidpId = params.get('fidpId');
    const stateParamObjStr = params.get('stateParamObj');
    const isFromContestPage = params.get('isFromContestPage');
    if (isFromContestPage) {
      setCookie(IS_FROM_CONTEST_PAGE, isFromContestPage);
    }

    if (fidpId && stateParamObjStr) {
      const stateParamObj = JSON.parse(decodeURIComponent(stateParamObjStr));

      const modifiedStateParamObj = {
        ...stateParamObj,
        returnToUrl,
        returnToOrg,
      };

      window.opener.postMessage({ fidpId, modifiedStateParamObj }, '*');
      handleSignUp(fidpId, modifiedStateParamObj);
    }
  }, []);

  if (isLoading) {
    return <PreLoader />;
  }

  return <></>;
}

export default SignupEmbeddedHandler;
